import { useEffect, useState } from "react";
import api from "../services/api";

// O carregamento de imagens é opcional: falhas do S3 nunca derrubam a sessão.
// Uma única requisição por imagem/escopo, com concorrência limitada e pausa
// temporária durante indisponibilidade da infraestrutura.
const validId = value => /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(String(value || ""));
const success = new Map();
const failures = new Map();
const inFlight = new Map();
const epochs = new Map();
const queue = [];
let running = 0;
let outageUntil = 0;
const MAX_CONCURRENT = 3;
const SUCCESS_TTL = 5 * 60 * 1000;

// Hash apenas para particionar a cache por JWT; nunca guardar tokens no mapa.
function sessionFingerprint() {
  const token = localStorage.getItem("token") || "";
  if (!token) return "";
  let hash = 2166136261;
  for (let i = 0; i < token.length; i++) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return String(hash >>> 0);
}

function resourceId(tipo, id) {
  return `${tipo}:${String(id).toLowerCase()}`;
}

function makeKey(tipo, id, revision) {
  const resource = resourceId(tipo, id);
  return `${sessionFingerprint()}:${resource}:v${epochs.get(resource) || 0}:r${revision}`;
}

function clearImage(tipo, id) {
  // Upload confirmado: permitir uma nova tentativa imediata desta imagem.
  outageUntil = 0;
  // Invalidar exclusivamente a imagem alterada, em todos os contextos locais.
  const resource = resourceId(tipo, id);
  epochs.set(resource, (epochs.get(resource) || 0) + 1);
  const marker = `:${resource}:v`;
  for (const [key, item] of success) {
    if (!key.includes(marker)) continue;
    success.delete(key);
    // O URL revogado só após os componentes receberem o novo estado.
    setTimeout(() => URL.revokeObjectURL(item.url), 1000);
  }
  for (const key of failures.keys()) if (key.includes(marker)) failures.delete(key);
}

function pump() {
  while (running < MAX_CONCURRENT && queue.length) {
    const job = queue.shift();
    if (Date.now() < outageUntil) {
      job.resolve("");
      continue;
    }
    running++;
    job.run().then(job.resolve, job.reject).finally(() => {
      running--;
      pump();
    });
  }
}

function loadLogo(tipo, id, revision = 0) {
  const key = makeKey(tipo, id, revision);
  if (!sessionFingerprint()) return Promise.resolve("");
  const now = Date.now();
  const cached = success.get(key);
  if (cached && cached.expiresAt > now) return Promise.resolve(cached.url);
  const failure = failures.get(key);
  if (failure && failure > now) return Promise.resolve("");
  if (now < outageUntil) return Promise.resolve("");
  if (inFlight.has(key)) return inFlight.get(key);

  const request = new Promise((resolve, reject) => {
    queue.push({resolve, reject, run: () => api.get(`/api/foto/${tipo}/${id}/arquivo`, {
      responseType: "blob",
      // Uma imagem não pode provocar refresh nem logout por 401.
      skipAuthRefresh: true,
      timeout: 12000
    }).then(response => {
      if (response.status === 204) {
        failures.set(key, Date.now() + 300000);
        return "";
      }
      const blob = response.data;
      if (!(blob instanceof Blob) || !["image/png", "image/jpeg", "image/webp", "image/gif"].includes(blob.type)) return "";
      const url = URL.createObjectURL(blob);
      const previous = success.get(key);
      if (previous) URL.revokeObjectURL(previous.url);
      success.set(key, {url, expiresAt: Date.now() + SUCCESS_TTL});
      return url;
    }).catch(error => {
      const status = error?.response?.status;
      if (status === 503 || status === 502 || status === 504 || !error?.response) {
        outageUntil = Date.now() + 45000;
      }
      failures.set(key, Date.now() + (status === 404 ? 300000 : 45000));
      return "";
    })});
    pump();
  }).catch(() => "").finally(() => { inFlight.delete(key); });
  inFlight.set(key, request);
  return request;
}

export default function useProtectedLogo(tipo, id, revision = 0) {
  const [url, setUrl] = useState("");
  const [refreshRevision, setRefreshRevision] = useState(0);

  useEffect(() => {
    const onUpdated = event => {
      const detail = event?.detail || {};
      const sameResource = detail.tipo === tipo && String(detail.id).toLowerCase() === String(id).toLowerCase();
      // Uma unidade sem logo própria pode herdar a imagem da entidade.
      // Atualizar a entidade invalida os thumbnails das unidades visíveis.
      const inheritedLogoMayChange = tipo === "unidades" && detail.tipo === "entidades";
      if (sameResource || inheritedLogoMayChange) {
        clearImage(tipo, id);
        setRefreshRevision(value => value + 1);
      }
    };
    window.addEventListener("gestor:logo-updated", onUpdated);
    return () => window.removeEventListener("gestor:logo-updated", onUpdated);
  }, [tipo, id]);

  useEffect(() => {
    let mounted = true;
    setUrl("");
    if (validId(id) && ["entidades", "unidades", "usuarios"].includes(tipo)) {
      loadLogo(tipo, id, revision).then(value => { if (mounted) setUrl(value); });
    }
    return () => { mounted = false; };
  }, [tipo, id, revision, refreshRevision]);
  return url;
}
